import type { Session } from "next-auth";
import { ORGANIZATION_ROLE } from "@/constants/organization-role";

/** Split theme/menu claim strings (`"a, b"` or `"a b"`) into tokens. Exported for nav item matching. */
export function parseClaimsString(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Resolves which project’s theme drives menu gating: the active selection when it’s
 * still in the user’s assignment list, otherwise the first assigned project.
 */
function effectiveMenuProjectId(
  session: Session | null,
  activeProjectId: string | null,
): string | null {
  const projects = session?.user?.projects ?? [];
  if (projects.length === 0) return null;
  if (activeProjectId && projects.some((p) => p.id === activeProjectId)) {
    return activeProjectId;
  }
  return projects[0]?.id ?? null;
}

/**
 * Claim tokens for menu visibility: **theme** tokens come only from the **selected**
 * (or default first) project’s `claims` on the JWT. `'admin'` is added for organization
 * admins (any organization) and for effective project **role** ADMIN. When the user has no
 * project assignments, only organization-admin `admin` applies (no theme tokens).
 */
export function menuClaimsFromSession(
  session: Session | null,
  activeProjectId: string | null,
): string[] | null {
  if (!session?.user) return null;

  const set = new Set<string>();
  const projects = session.user.projects ?? [];

  for (const t of session.user.organizations ?? []) {
    if (t.isAdmin) set.add("admin");
  }

  const pid = effectiveMenuProjectId(session, activeProjectId);
  if (pid) {
    const p = projects.find((x) => x.id === pid);
    if (p) {
      for (const c of parseClaimsString(p.claims)) {
        set.add(c);
      }
      if (p.role === ORGANIZATION_ROLE.ADMIN) {
        set.add("admin");
      }
    }
  }

  return [...set];
}

/** True once `activeProjectId` matches the org-scoped project list (including an empty list). */
export const isActiveProjectSettled = (
  scopedProjects: readonly { id: string }[],
  activeProjectId: string | null,
): boolean => {
  if (scopedProjects.length === 0) return activeProjectId === null;
  return scopedProjects.some((project) => project.id === activeProjectId);
};

/**
 * Menu claims for the org-scoped project list.
 * An empty list must not fall through to `menuClaimsFromSession`, which would use
 * another organization's first project.
 */
export const menuClaimsForScopedProjects = (
  session: Session | null,
  activeProjectId: string | null,
  scopedProjects: readonly { id: string }[],
): string[] | null => {
  if (scopedProjects.length === 0) return [];
  return menuClaimsFromSession(session, activeProjectId);
};

/**
 * Subtitle under the user name (sidebar auth control): active organization name when
 * still in the user’s list, otherwise the first organization, else email.
 */
export function menuContextLabelFromSession(
  session: Session | null,
  activeOrganizationId?: string | null,
): string {
  const organizations = session?.user?.organizations ?? [];
  if (organizations.length > 0) {
    const active =
      activeOrganizationId != null
        ? organizations.find((t) => t.id === activeOrganizationId)
        : undefined;
    return active?.name ?? organizations[0]?.name ?? "";
  }
  const email = session?.user?.email?.trim();
  if (email) return email;
  return "";
}

/**
 * Sidebar auth subtitle: context label plus an explicit organization-admin marker when applicable.
 * `organizationAdminLabel` should be localized by the caller (e.g. `$text`).
 */
export function menuSidebarSubtitleFromSession(
  session: Session | null,
  organizationAdminLabel: string,
  activeOrganizationId?: string | null,
): string {
  const base = menuContextLabelFromSession(session, activeOrganizationId);
  const isOrganizationAdmin = (session?.user?.organizations ?? []).some((t) => t.isAdmin);
  if (!isOrganizationAdmin) return base;
  return base ? `${base} · ${organizationAdminLabel}` : organizationAdminLabel;
}
